/**
 * Interactive question card for AI chat.
 *
 * Renders a question with clickable pill/chip options in a compact
 * horizontal wrapping layout. Optionally includes an "Other" text input
 * for free-form answers. Supports multiselect.
 */
import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Send, Check } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';

interface QuestionCardProps {
  question: string;
  options: string[];
  allowOther?: boolean;
  /** Allow selecting multiple options */
  multiSelect?: boolean;
  /** Index of the selected option (-1 for "other"). For multiSelect, this is the first selected. */
  selectedIndex?: number;
  /** Custom text entered for the "other" option */
  selectedOtherText?: string;
  /** Whether this card is interactive (clickable) */
  interactive: boolean;
  /** Called when user selects an option (text + option index, -1 for "other") */
  onSelect: (text: string, optionIndex: number) => void;
}

export function QuestionCard({
  question,
  options,
  allowOther = false,
  multiSelect = false,
  selectedIndex,
  selectedOtherText,
  interactive,
  onSelect,
}: QuestionCardProps) {
  const { t } = useTranslation('aiChat');
  const [otherText, setOtherText] = useState('');
  const [checkedIndices, setCheckedIndices] = useState<Set<number>>(new Set());
  const hasSelection = selectedIndex !== undefined;

  // For multi-select past state: derive all selected indices from the comma-separated selectedOtherText
  const allSelectedIndices = useMemo(() => {
    if (selectedIndex === undefined) return new Set<number>();
    if (selectedIndex === -1) return new Set<number>(); // "Other" text
    if (!selectedOtherText || !selectedOtherText.includes(',')) {
      return new Set([selectedIndex]);
    }
    // Parse comma-separated selections
    const parts = selectedOtherText.split(',').map(p => p.trim().toLowerCase());
    const indices = new Set<number>();
    options.forEach((opt, i) => {
      if (parts.includes(opt.toLowerCase())) indices.add(i);
    });
    return indices.size > 0 ? indices : new Set([selectedIndex]);
  }, [selectedIndex, selectedOtherText, options]);

  const handleOptionClick = useCallback(
    (index: number) => {
      if (!interactive || hasSelection) return;
      if (multiSelect) {
        setCheckedIndices((prev) => {
          const next = new Set(prev);
          if (next.has(index)) next.delete(index);
          else next.add(index);
          return next;
        });
      } else {
        onSelect(options[index], index);
      }
    },
    [interactive, hasSelection, multiSelect, onSelect, options],
  );

  const handleMultiSubmit = useCallback(() => {
    if (!interactive || hasSelection || checkedIndices.size === 0) return;
    const selectedTexts = options
      .filter((_, i) => checkedIndices.has(i))
      .join(', ');
    onSelect(selectedTexts, checkedIndices.values().next().value as number);
  }, [interactive, hasSelection, checkedIndices, options, onSelect]);

  const handleOtherSubmit = useCallback(() => {
    const trimmed = otherText.trim();
    if (!interactive || hasSelection || !trimmed) return;
    onSelect(trimmed, -1);
  }, [interactive, hasSelection, otherText, onSelect]);

  const handleOtherKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleOtherSubmit();
      }
    },
    [handleOtherSubmit],
  );

  return (
    <div className="rounded-lg border bg-card shadow-sm overflow-hidden">
      {/* Question + chip options in compact layout */}
      <div className="px-3 py-2.5">
        <p className="text-sm font-semibold mb-2">{question}</p>

        {/* Options as horizontal wrapping chips */}
        <div className="flex flex-wrap gap-1.5">
          {options.map((option, index) => {
            const isSelected = allSelectedIndices.has(index);
            const isChecked = multiSelect && checkedIndices.has(index);
            const isDisabled = !interactive || (hasSelection && !isSelected);

            return (
              <button
                key={index}
                type="button"
                disabled={isDisabled && !multiSelect}
                onClick={() => handleOptionClick(index)}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm border transition-colors',
                  interactive && !hasSelection && 'hover:bg-accent hover:border-accent-foreground/20 cursor-pointer',
                  !isSelected && !isChecked && 'bg-background border-border text-foreground',
                  isSelected && 'bg-primary text-primary-foreground border-primary',
                  isChecked && !hasSelection && 'bg-primary/10 border-primary/40 text-primary',
                  isDisabled && !isSelected && !multiSelect && 'opacity-40 cursor-default',
                )}
              >
                {multiSelect && !hasSelection ? (
                  <span className={cn(
                    'flex-shrink-0 h-3.5 w-3.5 rounded border flex items-center justify-center',
                    isChecked ? 'bg-primary border-primary text-primary-foreground' : 'border-muted-foreground/40',
                  )}>
                    {isChecked && <Check className="h-2.5 w-2.5" />}
                  </span>
                ) : isSelected ? (
                  <Check className="h-3 w-3 shrink-0" />
                ) : null}
                <span>{option}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* "Other" free-text option */}
      {allowOther && (
        <div className="px-3 pb-2.5">
          {selectedIndex === -1 ? (
            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-primary text-primary-foreground border border-primary">
              <Check className="h-3 w-3 shrink-0 mr-1.5" />
              {selectedOtherText}
            </span>
          ) : !hasSelection ? (
            <div className="flex items-center gap-1.5">
              <Input
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
                onKeyDown={handleOtherKeyDown}
                placeholder={t('interactiveBlocks.otherPlaceholder')}
                className="h-8 text-sm bg-background/50 border-border/50"
                disabled={!interactive || hasSelection}
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                disabled={!interactive || hasSelection || !otherText.trim()}
                onClick={handleOtherSubmit}
                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-primary"
              >
                <Send className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : null}
        </div>
      )}

      {/* Multi-select submit button */}
      {multiSelect && interactive && !hasSelection && (
        <div className="px-3 pb-2.5 pt-0.5">
          <Button
            type="button"
            size="sm"
            disabled={checkedIndices.size === 0}
            onClick={handleMultiSubmit}
            className="w-full"
          >
            {t('interactiveBlocks.submitOther')}
            {checkedIndices.size > 0 && ` (${checkedIndices.size})`}
          </Button>
        </div>
      )}
    </div>
  );
}
